import styles from './ProductSolution.module.scss'
import { useEffect, useRef } from 'react'
import { gsap, Power4 } from 'gsap'
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger'
gsap.registerPlugin(ScrollTrigger)

const tweenVars: gsap.TimelineVars = [
  {
    y: '50',
    duration: 1,
    opacity: 0,
    ease: Power4.easeOut,
  },
  {
    y: '50',
    opacity: 0,
    ease: Power4.easeOut,
    stagger: 0.2,
  },
]

const servicesTop = [
  {
    href: '/products/flamingo/',
    imgSrc: '/images/icons/icon_flamingo.svg',
    imgAlt: 'flamingo',
    titleJa: 'ウェブシステムモダナイゼーションツール',
    body: 'システム改修無しで既存のサービスのUX/UIのモダナイゼーション化を実現するツール「flamingo」を提供します。',
  },
  {
    href: '/products/zebra/',
    imgSrc: '/images/icons/icon_zebra.svg',
    imgAlt: 'zebra',
    titleJa: 'プッシュ通知サービスZEBRA',
    body: '顧客属性DBを活用し、細やかなセグメント配信で効果的なコミュニケーションを実現するツール「ZEBRA」を提供します。',
  },
]
const servicesList = [
  {
    src: '/services/amazonecs',
    img: '/images/LP/LP202407/Logo/LogoAmazonECS.png',
    service: 'Amazon ECS',
    text: 'Amazon ECSで実現する次世代のコンテナ管理',
    isImgOnly: false,
    body: '高可用性とスケーラビリティを兼ね備えた次世代のコンテナソリューションを提供します。',
  },
  {
    src: '/services/awsapprunner',
    img: '/images/LP/LP202407/Logo/LogoAWSAppRunner.png',
    service: 'AWS AppRunner',
    text: 'AWS App Runnerで実現するスムーズなサービス展開',
    isImgOnly: false,
    body: 'コードをプッシュするだけで、開発から運用までシンプルに、かつスケーラブルで安全なWebアプリケーションを展開します。',
  },
  {
    src: '/services/awscodepipeline',
    img: '/images/LP/LP202407/Logo/LogoAWSCodePipeline.png',
    service: 'AWS CodePipeline',
    text: 'AWS CodePipelineで実現する次世代の開発フロー',
    isImgOnly: false,
    body: 'ソースコードの変更からリリースまで、AWSサービスと完全統合された柔軟なCI/CDパイプラインを提供します。',
  },
  {
    src: '/services/awsfargate',
    img: '/images/LP/LP202407/Logo/LogoAWSFargate.png',
    service: 'AWS Fargate',
    text: 'AWS Fargateで実現するサーバーレスコンテナ環境',
    isImgOnly: false,
    body: 'インフラ管理の負担から解放され、アプリケーション開発に集中できる環境を提供します。',
  },
  {
    src: '/services/gitlab',
    img: '/images/LP/LP202407/Logo/LogoGitLab.png',
    service: 'GitLab',
    text: 'GitLabで実現する効率的なDevOpsワークフロー',
    isImgOnly: false,
    body: '計画、開発、テスト、デプロイまで、開発ライフサイクルのすべてを1つのプラットフォームで統合します。',
  },
  {
    src: '/services/githubactions',
    img: '/images/LP/LP202407/Logo/LogoGitHubActions.png',
    service: 'GitHub Actions',
    text: 'GitHub Actionsで実現する効率的なCI/CD',
    isImgOnly: false,
    body: 'リポジトリに統合された強力な自動化ツールで、開発からデプロイまでをシームレスに連携できます。',
  },
  {
    src: '/services/circleci',
    img: '/images/LP/LP202407/Logo/LogoCircleCI.png',
    service: 'CircleCI',
    text: 'CircleCIで開発サイクルを加速',
    isImgOnly: false,
    body: 'クラウドネイティブ環境で、ビルドとテストを並列化し、開発からデプロイまでを高速で柔軟に実現します。',
  },
]

const ProductSolution: React.FC = () => {
  const timeline = gsap.timeline()

  const title = useRef<HTMLHeadingElement>(null)
  const sub_title = useRef<HTMLHeadingElement>(null)
  const title_text = useRef<HTMLHeadingElement>(null)
  const title_sub_text = useRef<HTMLParagraphElement>(null)
  const img = useRef<HTMLImageElement>(null)
  const supports = useRef<HTMLDivElement[]>([])

  useEffect(() => {
    timeline
      .from(img.current, tweenVars[0], 0.1)
      .from(title.current, tweenVars[0], 0.1)
      .from(sub_title.current, tweenVars[0], 0.3)
      .from(title_text.current, tweenVars[0], 0.6)
      .from(title_sub_text.current, tweenVars[0], 0.8)
      .from(supports.current, tweenVars[0], 1.0)

    ScrollTrigger.create({
      animation: timeline,
      trigger: title.current,
      start: 'top-=150 80%',
      toggleActions: 'play none none reverse',
    })

    return () => {
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill())
      timeline.kill()
    }
  }, [])

  const ServicesTop = servicesTop.map((service, index) => (
    <div key={index} ref={(el) => (supports.current[index] = el)} className={styles.support}>
      <a href={service.href}>
        <div className={styles.support_wrapper}>
          <div className={styles.support_wrapper_head}>
            <figure className={styles.support_wrapper_head_logo}>
              <img src={service.imgSrc} alt={service.imgAlt} style={{ height: '40px', width: '160px' }} />
            </figure>
            <div className={styles.support_wrapper_head_title}>
              <p className={styles.support_wrapper_head_title_ja}>{service.titleJa}</p>
            </div>
            <p className={styles.support_wrapper_body}>{service.body}</p>
          </div>
          <span className={styles.support_wrapper_viewmore}>Read more</span>
        </div>
      </a>
    </div>
  ))

  const Services = servicesList.map((item, index) => (
    <div key={index} ref={(el) => (supports.current[index] = el)} className={styles.service}>
      <a href={item.src}>
        <div className={styles.service_wrapper}>
          <div className={styles.service_wrapper_head}>
            <div className={styles.service_wrapper_head_wrapper}>
              <div className={styles.service_wrapper_head_wrapper_logo}>
                {item.isImgOnly ? (
                  <>
                    <img src={item.img} alt={item.img} className={styles.img_only} />
                  </>
                ) : (
                  <>
                    <img src={item.img} alt={item.service} />
                    <p className={styles.service_wrapper_head_wrapper_logo_text}>{item.service}</p>
                  </>
                )}
              </div>
              <div className={styles.service_wrapper_head_wrapper_title}>
                <p>{item.text}</p>
              </div>
            </div>
            <p className={styles.service_wrapper_head_body}>{item.body}</p>
          </div>
          <div className={styles.service_wrapper_more}>Read more</div>
        </div>
      </a>
    </div>
  ))

  return (
    <section className={styles.contents} id="product-solution">
      <div className={styles.head}>
        <figure className={styles.head_icon}>
          <img ref={img} src="/images/icons/icon_product_solution.svg" alt="Digital innovation support" />
        </figure>
        <div className={styles.head_title}>
          <h2 ref={title} className={styles.head_title_en}>
            Product solution
          </h2>
          <h3 ref={sub_title} className={styles.head_title_ja}>
            プロダクトソリューション
          </h3>
        </div>
      </div>
      <p ref={title_text} className={styles.head_title_text}>
        モダンアプリケーションの実現に向けて、多様なプロダクトソリューションを提供しています。
        <br />
        自社開発サービスであるFlamingoやZEBRAをはじめ、GitLabによるDevOps環境構築、AWSマネージドサービスを活用したCI/CDパイプライン管理、コンテナ化ソリューションなど、幅広いソリューションを提供し、ソフトウェア開発とユーザー体験の向上を支援します。
      </p>
      <div className={styles.supports}>{ServicesTop}</div>
      <div className={styles.service_list}>{Services}</div>
    </section>
  )
}

export default ProductSolution
